import { Locale, SourceKind } from 'types/schema-generated-types';

export interface CommonConfigOptions {
  hiddenRoutes: string[];
  blacklistedBreadcrumbsPathParams: string[];
  contactInformation: { title: string; values: string[] }[];
  contactTopics: { name: string; tag?: string }[];
  locales: { label: string; value: string }[];
  privilegesGroups: {
    moduleLevel: {
      adminConsole: {
        any: string[];
        userManagement: string[];
        productsLicenses: string[];
        classesStudents: string[];
        organizations: string[];
        rosterManagement: string[];
        administratorHelp: string[];
      };
    };
    assignClassroomLicense: string[];
    assignOrgLicense: string[];
    createClassroom: string[];
    mutateClassroom: string[];
    createStudent: string[];
    mutateStudent: string[];
    mutateOrganization: string[];
    createUser: string[];
    mutateUser: string[];
    mutateAcademicSession: string[];
    updateClassroomLicense: string[];
    updateOrgLicense: string[];
  };
  validExternalIdSourceKinds: SourceKind[];
  endOfYear: {
    notificationDays: number;
    minAdvanceDays: number;
    limitDay: string;
  };
  reporting: {
    asyncPollIntervalMs: number;
  };
  combinedProductFamilyTiles: { productFamilyCodes: string[]; name: string }[];
  appTileOverrides: { app: string; supppressingApp: string }[];
}

const blacklistedBreadcrumbsPathParams = ['modalView'];
const hiddenRoutes: string[] = [];
const contactInformation = [
  {
    title: 'Office Hours',
    values: ['Monday - Friday', '8:30 am - 5:30 pm (ET)']
  },
  {
    title: 'August & September Hours',
    values: ['Monday - Thursday', '8:30 am - 7:30 pm (ET)', 'Friday', '8:30 am - 5:30 pm (ET)']
  },
  {
    title: 'Phone',
    values: ['301.263.2700', '1.888.983.8409']
  },
  {
    title: 'Email',
    values: ['technology@lwtears.com']
  }
];

const contactTopics = [
  { name: 'Getting Started – Account Management' },
  { name: 'Getting Started - User Management' },
  { name: 'Getting Started - Class Management' },
  { name: 'Getting Started - Student Management' },
  { name: 'District/School – Roster Management for New School Year' },
  { name: '+Live Insights Support' },
  { name: 'KWT Program Assistance' },
  { name: 'GSS (PreK) Program Assistance' },
  { name: 'HWT Program Assistance' },
  { name: 'Wet-Dry-Try Program Assistance' },
  { name: 'Pricing Information' }
];

const privilegesGroups = {
  moduleLevel: {
    adminConsole: {
      // TODO: could probably build a pattern-matcher to reduce duplication
      any: [
        'admin-console.user-management.read.org',
        'admin-console.products-licenses.read.org',
        'admin-console.classes-students.read.org',
        'admin-console.organizations.read.org',
        'admin-console.roster-management.read.org',
        'admin-console.administrator-help.read.org'
      ],
      userManagement: ['admin-console.user-management.read.org'],
      productsLicenses: ['admin-console.products-licenses.read.org'],
      classesStudents: ['admin-console.classes-students.read.org'],
      organizations: ['admin-console.organizations.read.org'],
      rosterManagement: ['admin-console.roster-management.read.org'],
      administratorHelp: ['admin-console.administrator-help.read.org']
    }
  },
  assignClassroomLicense: [
    'keyboarding-license.assign.classroom',
    'gss-license.assign.classroom',
    'wdt-license.assign.classroom',
    'hwt-license.assign.classroom',
    'azsapp-license.assign.classroom',
    'prm-license.assign.classroom'
  ],
  assignOrgLicense: [
    'keyboarding-license.assign.org',
    'gss-license.assign.org',
    'prekitt-license.assign.org',
    'wdt-license.assign.org',
    'azsapp-license.assign.org',
    'hwt-license.assign.org',
    'aztapp-license.assign.org',
    'azsapp-license.assign.org',
    'idtt-license.assign.org',
    'prm-license.assign.org'
  ],
  createClassroom: ['classroom.create.org'],
  mutateClassroom: ['classroom.write.classroom', 'classroom.write.org'],
  createStudent: ['student.create.classroom', 'student.create.org'],
  mutateStudent: ['student.write.classroom', 'student.write.org'],
  mutateOrganization: ['organization.write.org'],
  createUser: ['user.create.org'],
  mutateUser: ['user.write.org'],
  mutateAcademicSession: ['academic-session.write.org'],
  updateClassroomLicense: [
    'keyboarding-license.update-curriculum.classroom',
    'gss-license.update-curriculum.classroom',
    'wdt-license.update-curriculum.classroom',
    'hwt-license.update-curriculum.classroom',
    'azsapp-license.update-curriculum.classroom',
    'prm-license.update-curriculum.classroom'
  ],
  updateOrgLicense: [
    'keyboarding-license.update-curriculum.org',
    'gss-license.update-curriculum.org',
    'prekitt-license.update-curriculum.org',
    'wdt-license.update-curriculum.org',
    'azsapp-license.update-curriculum.classroom',
    'hwt-license.update-curriculum.org',
    'aztapp-license.update-curriculum.org',
    'azsapp-license.update-curriculum.org',
    'idtt-license.update-curriculum.org',
    'prm-license.update-curriculum.org'
  ]
};

const locales = [
  { label: 'English', value: Locale.EN_US },
  { label: 'Español', value: Locale.ES_US }
];

const validExternalIdSourceKinds = [SourceKind.GOOGLE];

const endOfYear = {
  notificationDays: 90,
  minAdvanceDays: 5,
  limitDay: '12/31'
};

const reporting = {
  asyncPollIntervalMs: 3000
};

const combinedProductFamilyTiles = [
  { productFamilyCodes: ['GSS', 'PREKITT'], name: 'Get Set for School' }
];

const appTileOverrides = [{ app: 'hwt-educator-22', supppressingApp: 'hwt-educator-25' }];

const CommonConfig: CommonConfigOptions = {
  hiddenRoutes,
  blacklistedBreadcrumbsPathParams,
  contactInformation,
  contactTopics,
  privilegesGroups,
  locales,
  validExternalIdSourceKinds,
  endOfYear,
  reporting,
  combinedProductFamilyTiles,
  appTileOverrides
};

export default CommonConfig;
